import {
  createContext, useContext, useEffect, useRef, useState, type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { authApi, type Sesion } from './api';
import { MENSAJES, registrarCaducidadDeSesion } from './errores';

interface Ctx {
  sesion: Sesion | null;
  cargando: boolean;
  entrar: (u: string, p: string) => Promise<void>;
  salir: () => Promise<void>;
  /**
   * db/080 · El usuario pone su propia clave. Los errores se DEJAN SUBIR: los
   * pinta el formulario (`CambiarClave.tsx`), que es quien sabe dónde ponerlos.
   */
  cambiarClave: (actual: string, nueva: string) => Promise<void>;
  /** db/080 · «Ya vi el recorrido»: para que no vuelva a saltar solo. */
  marcarRecorridoVisto: () => void;
  /** Equivalente a PMM.pgOK() del monolito v61. */
  puedeVer: (paginaId: string) => boolean;
  /** Equivalente a PMM.modOK(). */
  tieneModulo: (moduloId: string) => boolean;
  /**
   * Por qué se está viendo el acceso, cuando no se llegó a él pulsando «Salir».
   * Hoy solo lo escribe la caducidad de la sesión, y es lo que hacía falta el
   * 2026-08-31: sin esto el portal devolvía al login sin decir por qué, que es
   * apenas mejor que no devolver a nadie.
   */
  avisoSalida: string | null;
  /**
   * 2026-10-26 · La frase motivacional: `true` desde que `entrar()` acaba bien
   * hasta que la persona la cierra. Vive en memoria y no en el navegador a
   * propósito: sale al INICIAR SESIÓN, no al recargar ni al abrir otra pestaña
   * con la sesión ya abierta. Y si toca el portón de la clave, espera detrás de
   * él, porque `Marco` no pinta la frase hasta que el portón se baja.
   */
  fraseDeEntrada: boolean;
  cerrarFraseDeEntrada: () => void;
  /**
   * 2026-10-26 · Los pendientes que DHALIA enseña DESPUÉS de la frase. Se
   * ofrecen en cada inicio de sesión —la usuaria: «que les salgan cada vez que
   * inicien sesión hasta que las acciones sean realizadas»— y lo de «hasta que»
   * lo resuelve el servidor contando en vivo, no una marca aquí.
   */
  avisoPendientes: boolean;
  cerrarAvisoPendientes: () => void;
  /**
   * 2026-11-04 (db/286) · El podio de promociones, ENTRE la frase y los
   * pendientes (la usuaria: «después de la frase motivacional y antes de la lista
   * de pendientes»). Se ofrece en cada inicio de sesión; si sale o no lo decide el
   * servidor (`/api/podio-promos`), que sabe quién está en el podio y quién ya lo
   * vio. Mientras esté en `true`, los pendientes esperan.
   */
  avisoPodio: boolean;
  cerrarAvisoPodio: () => void;
}

const SesionCtx = createContext<Ctx | null>(null);

export function ProveedorSesion({ children }: { children: ReactNode }) {
  const [sesion, setSesion] = useState<Sesion | null>(null);
  const [cargando, setCargando] = useState(true);
  const [avisoSalida, setAvisoSalida] = useState<string | null>(null);
  const [fraseDeEntrada, setFraseDeEntrada] = useState(false);
  const [avisoPendientes, setAvisoPendientes] = useState(false);
  const [avisoPodio, setAvisoPodio] = useState(false);
  const qc = useQueryClient();

  /*
   * Espejo de `sesion` para el aviso de caducidad, que llega desde FUERA de
   * React (el cliente HTTP) y necesita saber si había alguien dentro. Se asigna
   * en el render y no en un efecto: un ref que se rellena en `useEffect` es la
   * trampa de StrictMode que ya costó una sesión en este proyecto —el doble
   * montaje deja el valor de la limpieza— y aquí no hace falta correr ese riesgo
   * para copiar un valor que ya está calculado.
   */
  const sesionRef = useRef<Sesion | null>(null);
  sesionRef.current = sesion;

  useEffect(() => {
    authApi.me()
      .then(setSesion)
      .catch(() => setSesion(null))
      .finally(() => setCargando(false));
  }, []);

  /*
   * LA SESIÓN CADUCADA DEVUELVE AL ACCESO (2026-08-31). El token dura 8 h fijas
   * desde la entrada y NO se renueva con el uso (`auth.ts`, `TTL`), así que a
   * todo el que trabaje una jornada larga se le muere a mitad de faena. Hasta
   * hoy el portal se quedaba donde estaba pintando el error del servidor en
   * rojo; el asesor 518 estuvo así con un acta de devolución escrita.
   *
   * `sesionRef.current` es la condición que evita el falso positivo obvio: el
   * `me()` del arranque contesta 401 cuando NADIE ha entrado todavía, y eso no
   * es una sesión caducada — es la pantalla de acceso, y ahí no se avisa nada.
   *
   * `qc.clear()` por el mismo motivo que en `entrar`/`salir`: lo que quede en la
   * caché de React Query son las cifras de la sesión que acaba de morir.
   */
  useEffect(() => {
    registrarCaducidadDeSesion((mensaje) => {
      if (!sesionRef.current) return;
      sesionRef.current = null;
      qc.clear();
      setSesion(null);
      setAvisoSalida(mensaje);
    });
    return () => registrarCaducidadDeSesion(null);
  }, [qc]);

  /*
   * EL CIERRE AUTOMÁTICO A LAS 8 H (2026-10-05). Lo de arriba solo reacciona
   * cuando la persona PULSA algo con la sesión ya muerta, y entonces lo que ve
   * es «Sesión caducada». La usuaria pidió que la sesión vencida se cierre sola:
   * a la hora exacta el portal vuelve al acceso por su cuenta, con un aviso que
   * dice que terminaron las 8 horas, sin esperar a un clic que dé error.
   *
   * El plazo lo da el servidor como DURACIÓN (`venceEnSeg`, ver `auth-me.mts`)
   * y se cuenta desde que llegó la respuesta. Las sesiones no se renuevan con el
   * uso, así que no hace falta reprogramar nada: se fija al entrar o al recargar.
   *
   * Se comprueba cada 15 s, y además al volver a la pestaña, en vez de un solo
   * `setTimeout` de 8 h: el navegador CONGELA los temporizadores de una pestaña
   * en segundo plano y de un portátil suspendido, y el cierre llegaría tarde —
   * justo en el caso de quien deja el portal abierto y vuelve al día siguiente.
   * `Date.now()` sí salta al despertar, por eso se compara contra él.
   *
   * Se cierra UN MINUTO ANTES del vencimiento, y es a propósito: con la cookie
   * todavía viva, el `logout()` queda apuntado a nombre del usuario y la
   * Bitácora enseña una salida MEDIDA. Al vencer, el servidor ya no sabe quién
   * era y la vista de sesiones solo puede decir «no registrada · venció ~…» —que
   * es lo que la usuaria leyó el 2026-10-05 como «a todos les da sesión
   * vencida»: de 424 entradas en 8 días solo 117 tenían salida registrada.
   */
  const venceEnSeg = sesion?.venceEnSeg;
  useEffect(() => {
    if (venceEnSeg === undefined) return;
    const limite = Date.now() + Math.max(0, venceEnSeg - 60) * 1000;
    const revisar = () => {
      if (Date.now() < limite || !sesionRef.current) return;
      sesionRef.current = null;
      void authApi.logout().catch(() => {});
      qc.clear();
      setSesion(null);
      setAvisoSalida(MENSAJES.sesionCerrada);
    };
    const reloj = window.setInterval(revisar, 15_000);
    document.addEventListener('visibilitychange', revisar);
    window.addEventListener('focus', revisar);
    revisar();
    return () => {
      window.clearInterval(reloj);
      document.removeEventListener('visibilitychange', revisar);
      window.removeEventListener('focus', revisar);
    };
  }, [venceEnSeg, qc]);

  const valor: Ctx = {
    sesion,
    cargando,
    /*
     * `qc.clear()` en CADA cambio de identidad, y no es precaución teórica.
     * El QueryClient (main.tsx) tiene staleTime 5 min y gcTime 30 min, y en todo
     * `src` no había una sola llamada a clear/reset: al salir de una cuenta y
     * entrar en otra desde la MISMA pestaña —que es exactamente lo que se hace
     * con la clave maestra, varias veces seguidas— React Query servía de caché
     * las cifras de la cuenta anterior en las pantallas ya visitadas, mezcladas
     * con las nuevas. Nadie recarga la página entre medias porque el login es
     * parte de la SPA: `salir()` solo hace setSesion(null).
     *
     * Es el fallo más probable de esta tanda y el más difícil de notar: no da
     * error, da una cifra creíble y equivocada.
     */
    entrar: async (u, p) => {
      qc.clear();
      setSesion(await authApi.login(u, p));
      setAvisoSalida(null);
      setFraseDeEntrada(true);
      setAvisoPodio(true);
      setAvisoPendientes(true);
    },
    salir: async () => {
      await authApi.logout();
      qc.clear();
      setSesion(null);
      setFraseDeEntrada(false);
      setAvisoPodio(false);
      setAvisoPendientes(false);
      /* Salir por la puerta no deja aviso: el de caducidad explica algo que la
       * persona no hizo, y este lo hizo ella. */
      setAvisoSalida(null);
    },
    /*
     * NO se llama a `qc.clear()` en el caso normal, a diferencia de
     * `entrar`/`salir`: la identidad no cambia al cambiar la clave, así que la
     * caché de React Query sigue siendo de esta misma persona y tirarla
     * obligaría a recargar cada pantalla ya visitada sin ninguna ganancia.
     *
     * **SÍ se limpia al bajar el PORTÓN**, y por un motivo distinto del de
     * `entrar`: lo que hay en la caché en ese momento no son cifras de otra
     * cuenta, son los **403 del portón**. React Query cachea también los fallos,
     * así que sin este clear el usuario cruza el portón y aterriza en pantallas
     * que dicen «tienes que crear tu contraseña» leídas de una caché ya caduca.
     * Y va DESPUÉS del `await`: un intento fallido no le tira la caché a quien
     * está cambiando la clave por gusto.
     *
     * Lo que se refresca es `usuario` con lo que devuelve el servidor —no con un
     * `true` puesto a mano— porque `clavePersonalizada` es una columna GENERADA
     * (db/080): la única forma de que la cabecera diga lo mismo que la base es
     * preguntárselo a la base. Y es ese mismo `debeCambiar: false` el que hace
     * que `Marco` deje de pintar el portón, sin navegar a ningún sitio.
     */
    cambiarClave: async (actual, nueva) => {
      const veniaDelPorton = !!sesion?.usuario.debeCambiar;
      const r = await authApi.cambiarClave(actual, nueva);
      if (veniaDelPorton) qc.clear();
      setSesion((s) => (s ? { ...s, usuario: { ...s.usuario, ...r.usuario } } : s));
    },
    /*
     * Optimista a propósito, y en este orden: primero se apaga en pantalla y
     * después se avisa al servidor. Si la petición fallara (red caída), lo peor
     * que pasa es que el recorrido vuelva a ofrecerse en la próxima entrada —
     * mientras que esperar la respuesta para cerrar el recorrido dejaría al
     * usuario mirando una pantalla que no reacciona.
     */
    marcarRecorridoVisto: () => {
      setSesion((s) => (s && !s.usuario.recorridoVisto
        ? { ...s, usuario: { ...s.usuario, recorridoVisto: true } }
        : s));
      void authApi.recorridoVisto().catch((e) => {
        console.warn('No se pudo guardar que el recorrido ya se vio', e);
      });
    },
    puedeVer: (pagina) => !!sesion?.paginas.includes(pagina),
    tieneModulo: (modulo) => !!sesion?.modulos.includes(modulo),
    avisoSalida,
    fraseDeEntrada,
    cerrarFraseDeEntrada: () => setFraseDeEntrada(false),
    avisoPendientes,
    cerrarAvisoPendientes: () => setAvisoPendientes(false),
    avisoPodio,
    cerrarAvisoPodio: () => setAvisoPodio(false),
  };

  return <SesionCtx.Provider value={valor}>{children}</SesionCtx.Provider>;
}

export function useSesion(): Ctx {
  const c = useContext(SesionCtx);
  if (!c) throw new Error('useSesion debe usarse dentro de <ProveedorSesion>');
  return c;
}
